import {debugAssert} from 'Asserts';
import {CidIdMap} from 'notebook/notes/NoteConstants';

export const UPDATE_ALL_ITEM_PARTS = -1;

/**
 * Used for items that support incremental updates. This function maps the item to its parts and part IDs.
 */
export type GetItemParts<ItemState, ItemStatePart> = (
  item: ItemState
) => Array<{itemPartId: string; itemPart: ItemStatePart}>;

export interface CidItemPartMap<ItemStatePart> {
  [cid: string]: ItemStatePart;
}

export class ItemPartSaveTracker<ItemState = unknown, ItemStatePart = unknown> {
  private unsavedUpdateMap: CidItemPartMap<ItemStatePart> | typeof UPDATE_ALL_ITEM_PARTS = {};

  constructor(private readonly getItemParts: GetItemParts<ItemState, ItemStatePart> | undefined) {}

  getUnsavedItemParts(): Array<ItemStatePart> | typeof UPDATE_ALL_ITEM_PARTS {
    return this.unsavedUpdateMap !== UPDATE_ALL_ITEM_PARTS
      ? Object.values(this.unsavedUpdateMap)
      : UPDATE_ALL_ITEM_PARTS;
  }

  /**
   * Resets the ItemPartSaveTracker to start with a new CidIdMap as its baseline that it is tracking changes from.
   */
  reset(fileId: string | number, cidIdMap?: CidIdMap): void {
    debugAssert(() => Boolean(cidIdMap), 'No CidIdMap was provided', {extra: {fileId}});
    this.unsavedUpdateMap = {};
  }

  markAllForUpdate(): void {
    this.unsavedUpdateMap = UPDATE_ALL_ITEM_PARTS;
  }

  addUnsavedItemPart(updatedItem: ItemState, updatedItemPartIndexes?: Array<number>): void {
    if (updatedItemPartIndexes === undefined || this.getItemParts == null) {
      return;
    }
    const updatedItemParts = this.getItemParts(updatedItem);
    updatedItemPartIndexes.forEach((itemPartIndex) => {
      const changedItemPart = updatedItemParts?.[itemPartIndex];
      // Mark that this item will require all parts to be sent in the next save
      if (itemPartIndex === UPDATE_ALL_ITEM_PARTS || !changedItemPart) {
        this.unsavedUpdateMap = UPDATE_ALL_ITEM_PARTS;
      } else if (this.unsavedUpdateMap !== UPDATE_ALL_ITEM_PARTS) {
        const {itemPartId, itemPart} = changedItemPart;
        this.unsavedUpdateMap[itemPartId] = itemPart;
      }
    });
  }
}
